// Iter-194m — Close mission confirmation dialog.
// Shows the close-report (budget consumption, revenue, margin) and blocks the
// closure if any CRA submission containing entries on this mission is still
// in DRAFT/SUBMITTED state. The CEO can override with the "force" toggle.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

function fmt(n) {
  return (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function MissionCloseDialog({ open, missionId, currentUserIsCEO, onClose, onClosed }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [force, setForce] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!open || !missionId) { setReport(null); setForce(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/missions/${missionId}/close-report`);
        if (!cancelled) setReport(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Échec chargement rapport");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, missionId]);

  if (!open) return null;

  const handleClose = async () => {
    setClosing(true);
    try {
      const qs = force ? "?force=true" : "";
      const r = await api.post(`/missions/${missionId}/close${qs}`);
      toast.success(`Mission clôturée — CA ${fmt(r.data?.report?.revenue_invoiced || 0)} ${report?.currency || "MAD"}`);
      onClosed && onClosed();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : (detail || "Échec clôture"));
    } finally { setClosing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="mission-close-dialog">
        <DialogHeader>
          <DialogTitle>Clôturer la mission</DialogTitle>
          <DialogDescription>
            Revue du rapport de clôture avant validation. Une fois clôturée :
            les imputations CRA et les dépenses refacturables sur cette mission seront bloquées.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-8 justify-center">
            <Loader2 size={16} className="animate-spin" /> Calcul du rapport en cours…
          </div>
        )}

        {report && !loading && (
          <div className="space-y-4 py-2">
            {/* KPIs grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="mission-close-report">
              <KpiCell label="Budget JH" value={`${fmt(report.budget_jh)} j`} />
              <KpiCell label="Jours consommés" value={`${fmt(report.consumed_jh)} j`}
                hint={report.budget_consumption_pct !== null ? `${fmt(report.budget_consumption_pct)} % du budget` : null}
                tone={report.budget_consumption_pct > 100 ? "warn" : "ok"} />
              <KpiCell label="Restant" value={`${fmt(report.remaining_jh)} j`}
                tone={report.remaining_jh < 0 ? "warn" : "ok"} />
              <KpiCell label="CA facturé" value={`${fmt(report.revenue_invoiced)} ${report.currency}`}
                hint={report.expected_revenue ? `attendu : ${fmt(report.expected_revenue)}` : null} />
              <KpiCell label="CA encaissé" value={`${fmt(report.revenue_paid)} ${report.currency}`} />
              <KpiCell label="Restant à encaisser" value={`${fmt(report.revenue_outstanding)} ${report.currency}`}
                tone={report.revenue_outstanding > 0 ? "warn" : "ok"} />
              {report.effective_margin !== null && (
                <>
                  <KpiCell label="Coût estimé" value={`${fmt(report.cost_estimate || 0)} ${report.currency}`} />
                  <KpiCell label="Marge effective" value={`${fmt(report.effective_margin)} ${report.currency}`}
                    tone={report.effective_margin > 0 ? "ok" : "warn"} />
                  <KpiCell label="Marge %" value={`${fmt(report.effective_margin_pct)} %`}
                    tone={report.effective_margin_pct > 25 ? "ok" : "warn"} />
                </>
              )}
            </div>

            {/* Blocking CRAs */}
            {report.blocking_unvalidated_cras?.length > 0 ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="mission-close-blocking">
                <div className="flex items-start gap-2 text-red-900 text-sm font-semibold">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <span>{report.blocking_unvalidated_cras.length} feuille(s) de temps non validée(s) contenant des imputations sur cette mission</span>
                </div>
                <ul className="mt-2 text-xs text-red-800 space-y-1 pl-6 list-disc">
                  {report.blocking_unvalidated_cras.slice(0, 10).map((b, i) => (
                    <li key={i}>
                      <span className="font-mono">{b.user_name}</span> · {String(b.month).padStart(2, "0")}/{b.year} · statut <span className="font-mono">{b.status}</span>
                    </li>
                  ))}
                  {report.blocking_unvalidated_cras.length > 10 && (
                    <li className="italic">+ {report.blocking_unvalidated_cras.length - 10} de plus…</li>
                  )}
                </ul>
                {currentUserIsCEO && (
                  <label className="flex items-center gap-2 mt-3 text-xs text-red-900">
                    <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="mission-close-force" />
                    <span>Forcer la clôture malgré les feuilles non validées (CEO uniquement — à éviter)</span>
                  </label>
                )}
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2 text-sm text-emerald-900" data-testid="mission-close-ready">
                <CheckCircle2 size={16} /> Toutes les feuilles de temps sont validées — la clôture peut être confirmée.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
          <button
            onClick={handleClose}
            disabled={closing || loading || (!report?.can_close && !force)}
            data-testid="mission-close-confirm"
            className="px-4 h-9 text-sm bg-zinc-950 text-white rounded hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {closing ? "Clôture…" : (force ? "Forcer la clôture" : "Clôturer la mission")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KpiCell({ label, value, hint, tone }) {
  const toneCls = tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "text-zinc-900";
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className={`font-mono text-sm font-semibold ${toneCls}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
}
